import {
  eachMonthOfInterval,
  eachYearOfInterval,
  subYears,
  formatDistanceToNow,
  startOfDay,
  endOfDay,
  differenceInMinutes,
  differenceInHours,
  differenceInDays,
  differenceInSeconds,
} from "date-fns";

import { getAllTimezones } from "countries-and-timezones";
import { isDaylightSavingTime } from "@helpers/getScheduledTimezone";

import { purchaseDateFormat } from "@const/formats";
import formatWithLocale from "@helpers/formatWithLocale";

/**
 * Generates all months array
 * @returns 12 months array
 */
export const getMonths = () =>
  eachMonthOfInterval({
    start: new Date(1994, 0, 1),
    end: new Date(1994, 11, 1),
  });

/**
 * Get dates count in the month
 */
export function getDaysInMonth(year, month) {
  // Month parameter in JavaScript is 0-indexed (0 = January, 1 = February, ..., 11 = December)
  // So, we need to subtract 1 from the provided month.
  const date = new Date(year, month - 1, 1);

  // Move to the next month's first day and then move back one day to get the last day of the current month.
  date.setMonth(date.getMonth() + 1);
  date.setDate(date.getDate() - 1);

  return date.getDate();
}

/**
 * Generate days from 1 to 31
 * @returns 31 day
 */
export const getDays = (length = 31) => Array.from({ length }, (_, i) => i + 1);

/**
 * Generates ~100 years
 * @returns ~100 formated years array
 */
export const getYears = () =>
  eachYearOfInterval({
    start: subYears(new Date(), 101),
    end: subYears(new Date(), 17),
  })
    .reverse()
    .map((year) => formatWithLocale(year, "yyyy"));

/**
 * Generates hours
 * @returns from 00 to 23 array
 */
export const getHours = () => Array.from({ length: 24 }, (_, i) => i);

/**
 * Generates minutes
 * @returns from 00 to 59 array
 */
export const getMinutes = () =>
  Array.from({ length: 60 }, (_, i) => `0${i}`.slice(-2));

/**
 * Format date to ISOString
 * return ISO format date
 * @param date
 * @return {string}
 **/
export const getISODate = (date) => new Date(date).toISOString();

export const getLastDay = (date) => {
  return formatDistanceToNow(new Date(date), {
    addSuffix: true,
  });
};

export const getPeriods = (allTimeStart) => [
  {
    label: "Week",
    key: "week",
    period: [
      startOfDay(new Date(new Date().setDate(new Date().getDate() - 6))),
      endOfDay(new Date()),
    ],
  },
  {
    label: "Month",
    key: "month",
    period: [
      startOfDay(new Date(new Date().setDate(new Date().getDate() - 29))),
      endOfDay(new Date()),
    ],
  },
  {
    label: "Half a year",
    key: "halfYear",
    period: [
      startOfDay(new Date(new Date().setDate(new Date().getDate() - 179))),
      endOfDay(new Date()),
    ],
  },
  {
    label: "Year",
    key: "year",
    period: [
      startOfDay(new Date(new Date().setDate(new Date().getDate() - 365))),
      endOfDay(new Date()),
    ],
  },
  {
    label: "All time",
    key: "all",
    period: [allTimeStart, endOfDay(new Date())],
  },
  {
    label: "Custom period",
    key: "custom",
    period: [null, null],
  },
];

export const getPlusSixtyDays = (date) =>
  endOfDay(new Date(new Date().setDate(date.getDate() + 59)));

export const allTimeZones = Object.entries(getAllTimezones())
  .sort((a, b) => a[1].utcOffset - b[1].utcOffset)
  .map(([timeZoneName, { utcOffsetStr, dstOffsetStr }]) => {
    const newTimeZoneName =
      timeZoneName === "Europe/Kyiv" ? "Europe/Kiev" : timeZoneName;
    return {
      timeZoneName: newTimeZoneName,
      offsetStr: isDaylightSavingTime() ? dstOffsetStr : utcOffsetStr,
    };
  });

export const getFormattedTimezone = ({ timeZoneName, offsetStr }) => {
  if (!timeZoneName || !offsetStr) return "No time zone available";
  const newTimeZoneName =
    timeZoneName === "Europe/Kyiv" ? "Europe/Kiev" : timeZoneName;
  return `(UTC ${offsetStr}) ${newTimeZoneName}`;
};

const formatDate = (date) =>
  formatWithLocale(new Date(date), purchaseDateFormat);

export const getScheduledDateString = (date) => {
  const [day, rest] = date.split("T");
  const time = rest.slice(0, 5);
  const timeZone = rest.slice(5);

  const [year, month, dayDate] = day.split("-");

  return `${formatDate(
    new Date(year, month - 1, dayDate),
  )}, ${time} (UTC ${timeZone})`;
};

export const parseScheduledDateString = (date) => {
  if (!date) return {};
  const [day, rest] = date.split("T");
  const time = rest.slice(0, 5);
  const timeZoneOffset = rest.slice(5);

  return { day, time, timeZoneOffset };
};

export const formatDay = (day) => {
  if (!day) return;
  const [year, month, dayDate] = day.split("-");
  return formatDate(new Date(year, month - 1, dayDate));
};

export const formatRelativeTime = (isoDate) => {
  const targetDate = new Date(isoDate);
  const now = new Date();

  // Calculate the differences
  const diffInMinutes = differenceInMinutes(targetDate, now);
  const diffInHours = differenceInHours(targetDate, now);
  const diffInDays = differenceInDays(targetDate, now);

  // Format the output based on the time difference
  if (diffInMinutes < 60) {
    return diffInMinutes < 0 ? "now" : `in ${diffInMinutes} mins`;
  } else if (diffInHours < 24) {
    return `in ${diffInHours} hours`;
  } else {
    return `in ${diffInDays} days`;
  }
};

export const convertTimeToSeconds = (streamTime) => {
  const [hours, minutes, seconds] = streamTime.split(":").map(Number);
  return hours * 3600 + minutes * 60 + seconds;
};

export const getTimeDifference = (createdAt, updatedAt) => {
  const diffInSeconds = differenceInSeconds(
    new Date(updatedAt),
    new Date(createdAt),
  );

  const hours = Math.floor(diffInSeconds / 3600)
    .toString()
    .padStart(2, "0");
  const minutes = Math.floor((diffInSeconds % 3600) / 60)
    .toString()
    .padStart(2, "0");
  const seconds = (diffInSeconds % 60).toString().padStart(2, "0");

  return `${hours}:${minutes}:${seconds}`;
};
